import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { BookOpen, FileText, Percent, Plus, Printer, Save, Trash2 } from "lucide-react"
import { useEffect, useState } from "react"
import { flushSync } from "react-dom"

import { api } from "@/api/cliente"
import type { Empresa } from "@/componentes/empresa/tipos"
import { Exportar } from "@/componentes/exportar/Exportar"
import type { DocumentoExportable } from "@/componentes/exportar/modelo"
import { CatalogoIslr } from "@/componentes/retenciones/CatalogoIslr"
import type { TipoComprobante } from "@/componentes/retenciones/ComprobanteRetencion"
import {
  ComprobanteRetencion,
  TIPOS_COMPROBANTE,
} from "@/componentes/retenciones/ComprobanteRetencion"
import { EditorRetencion } from "@/componentes/retenciones/EditorRetencion"
import { LectorRetencion } from "@/componentes/retenciones/LectorRetencion"
import { LineasRetencion } from "@/componentes/retenciones/LineasRetencion"
import type {
  BorradorRetencion,
  Evaluacion,
  ResumenRetencion,
  Retencion,
  ValoresPorOmision,
} from "@/componentes/retenciones/tipos"
import {
  borradorEnBlanco,
  comoBorrador,
  paraElServidor,
  posicionesEnviadas,
  vigente,
} from "@/componentes/retenciones/tipos"
import { monto } from "@/componentes/tesoreria/dinero"
import { Boton } from "@/componentes/ui/Boton"
import { useConfirmar } from "@/componentes/ui/Confirmar"
import { type Pestana, Pestanas } from "@/componentes/ui/Pestanas"
import { tienePermiso } from "@/lib/autorizacion"
import { comoFecha, hoyLocal } from "@/lib/fechas"
import { usarSesion } from "@/lib/sesion"

/**
 * Comprobantes de retención provisional: lo que el cliente le retiene a los terceros
 * del puerto. Sustituye a `Retencion provicionales.xlsx`.
 *
 * ⚠ **La pantalla no calcula: pregunta.** Cada cambio en líneas o porcentajes pide los
 * totales a `POST /calcular` (con una pausa de 300 ms para no pedir uno por tecla), y
 * la previa —que es la misma pieza que se imprime— pinta lo que contesta el servidor.
 * Así el comprobante guardado y el que se vio al escribirlo no pueden diferir.
 *
 * ⚠ **Con tasa de reajuste, el servidor calcula las DOS** —a emisión y al pago— y el
 * comprobante sale reajustado; aquí se enseñan lado a lado con su diferencia.
 *
 * ⚠ **Se imprime lo del FORMULARIO, guardado o no**, como en rotulación: obligar a
 * guardar para probar una hoja llenaría la bandeja de borradores.
 */
type Seccion = "comprobantes" | "catalogo"
const SECCIONES: readonly Pestana<Seccion>[] = [
  { valor: "comprobantes", etiqueta: "Comprobantes", icono: FileText },
  { valor: "catalogo", etiqueta: "Catálogo ISLR (Decreto 1.808)", icono: BookOpen },
]

/** Los comprobantes guardados, tal como los filtra la búsqueda, como documento exportable. */
function documentoGuardados(filas: ResumenRetencion[], busqueda: string): DocumentoExportable {
  return {
    titulo: "Retenciones provisionales",
    subtitulo: busqueda.trim()
      ? `Comprobantes guardados · búsqueda: «${busqueda.trim()}»`
      : "Comprobantes guardados",
    secciones: [
      {
        titulo: "Comprobantes guardados",
        columnas: [
          { titulo: "Código", tipo: "texto" },
          { titulo: "Fecha", tipo: "fecha" },
          { titulo: "Agente de retención", tipo: "texto" },
          { titulo: "Beneficiario", tipo: "texto" },
          { titulo: "Documento", tipo: "texto" },
          { titulo: "B/L", tipo: "texto" },
          { titulo: "Retenido", tipo: "monto" },
          { titulo: "Pagado", tipo: "monto" },
          { titulo: "Reajustado", tipo: "texto" },
        ],
        filas: filas.map((r) => ({
          celdas: [
            r.codigo,
            r.fecha.slice(0, 10),
            r.agente_nombre,
            r.beneficiario_nombre,
            r.documento,
            r.bl,
            Number(r.total_retenido),
            Number(r.neto),
            r.reajustado ? "Sí" : "No",
          ],
        })),
      },
    ],
  }
}

export function RetencionesProvisionales() {
  const [seccion, setSeccion] = useState<Seccion>("comprobantes")
  const clienteQuery = useQueryClient()
  const [confirmacion, confirmar] = useConfirmar()
  const usuario = usarSesion((e) => e.usuario)
  const puedeEditar = tienePermiso(usuario, "retencion_provisional:editar")
  // Leer con IA manda los archivos a Google: es la misma llave que las facturas.
  const puedeLeer = tienePermiso(usuario, "factura_ia:usar")
  const [modo, setModo] = useState<"manual" | "ia">("manual")
  const [vista, setVista] = useState<TipoComprobante>("ISLR")
  const [aImprimir, setAImprimir] = useState<TipoComprobante[]>([...TIPOS_COMPROBANTE])
  const [borrador, setBorrador] = useState<BorradorRetencion | null>(null)
  const [editando, setEditando] = useState<{ id: number; codigo: string } | null>(null)
  const [busqueda, setBusqueda] = useState("")
  const [aviso, setAviso] = useState<string | null>(null)

  const empresa = useQuery({
    queryKey: ["empresa"],
    queryFn: () => api.get<Empresa>("/api/v1/empresa"),
  })
  const valores = useQuery({
    queryKey: ["retenciones", "valores"],
    queryFn: () => api.get<ValoresPorOmision>(`/api/v1/retenciones-provisionales/valores`),
  })
  const lista = useQuery({
    queryKey: ["retenciones", "lista", busqueda],
    queryFn: () =>
      api.get<ResumenRetencion[]>(
        `/api/v1/retenciones-provisionales?busqueda=${encodeURIComponent(busqueda)}`,
      ),
  })

  // El formulario nace cuando se sabe con qué porcentajes nace.
  useEffect(() => {
    if (borrador === null && valores.data) setBorrador(borradorEnBlanco(valores.data, hoyLocal()))
  }, [borrador, valores.data])

  // ⚠ La pausa se lleva sobre el TEXTO y no sobre el objeto: un objeto nuevo en cada
  // pintado volvería a disparar el efecto sin fin.
  const paraCalcular = borrador === null ? null : JSON.stringify(aCalcular(borrador))
  const [enPausa, setEnPausa] = useState(paraCalcular)
  useEffect(() => {
    const t = setTimeout(() => setEnPausa(paraCalcular), 300)
    return () => clearTimeout(t)
  }, [paraCalcular])

  const calculo = useQuery({
    queryKey: ["retenciones", "calculo", enPausa],
    queryFn: () =>
      api.post<Evaluacion>(
        `/api/v1/retenciones-provisionales/calcular`,
        JSON.parse(enPausa ?? "{}"),
      ),
    enabled: enPausa !== null,
    placeholderData: keepPreviousData,
  })

  const guardar = useMutation({
    mutationFn: (b: BorradorRetencion) =>
      editando === null
        ? api.post<Retencion>("/api/v1/retenciones-provisionales", paraElServidor(b))
        : api.put<Retencion>(`/api/v1/retenciones-provisionales/${editando.id}`, paraElServidor(b)),
    onSuccess: (r) => {
      clienteQuery.invalidateQueries({ queryKey: ["retenciones", "lista"] })
      setEditando({ id: r.id, codigo: r.codigo })
      setBorrador(comoBorrador(r))
      setAviso(`Guardado como ${r.codigo}.`)
    },
    onError: (e: Error) => setAviso(e.message),
  })

  const abrir = useMutation({
    mutationFn: (id: number) => api.get<Retencion>(`/api/v1/retenciones-provisionales/${id}`),
    onSuccess: (r) => {
      setEditando({ id: r.id, codigo: r.codigo })
      setBorrador(comoBorrador(r))
      setAviso(null)
    },
    onError: (e: Error) => setAviso(e.message),
  })

  const borrar = useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/v1/retenciones-provisionales/${id}`),
    onSuccess: (_, id) => {
      clienteQuery.invalidateQueries({ queryKey: ["retenciones", "lista"] })
      if (editando?.id === id) empezarOtro()
    },
    onError: (e: Error) => setAviso(e.message),
  })

  function empezarOtro() {
    setEditando(null)
    setBorrador(borradorEnBlanco(valores.data, hoyLocal()))
    setAviso(null)
  }

  if (borrador === null) {
    return (
      <p
        className="text-sm"
        style={{ color: valores.error ? "var(--destructive)" : "var(--fg-muted)" }}
      >
        {valores.error ? valores.error.message : "Cargando…"}
      </p>
    )
  }

  const falta = faltante(borrador)
  const pieza = (tipo: TipoComprobante) => (
    <ComprobanteRetencion
      tipo={tipo}
      datos={borrador}
      calculo={calculo.data}
      codigo={editando?.codigo ?? null}
      empresa={empresa.data}
    />
  )
  const vigentes = calculo.data ? vigente(calculo.data).calculo : undefined
  const retenido: Record<TipoComprobante, string | undefined> = {
    ISLR: vigentes?.monto_ret_islr,
    IVA: vigentes?.monto_ret_iva,
  }
  // Los bolívares a emisión de cada línea del formulario, para las que vienen en dólares.
  const enviadas = posicionesEnviadas(borrador.lineas)
  const montosEmision = enviadas.map((n) =>
    n === null || calculo.data === undefined ? undefined : calculo.data.montos[n],
  )

  /**
   * ⚠ El pliego se pinta con lo elegido ANTES de abrir el diálogo: `flushSync` obliga a
   * React a escribir el DOM en el acto. Sin él, `print()` fotografiaría el pliego de la
   * impresión anterior.
   */
  function imprimir(tipos: TipoComprobante[]) {
    flushSync(() => setAImprimir(tipos))
    window.print()
  }

  return (
    <div>
      <header className="no-print mb-6">
        <h1 className="flex items-center gap-2 text-xl font-semibold">
          <Percent size={20} aria-hidden={true} />
          Retenciones provisionales
        </h1>
        <p className="mt-1 text-sm" style={{ color: "var(--fg-muted)" }}>
          Lo que el cliente le retiene a los terceros del puerto. Ajusta los porcentajes, marca qué
          servicios lleva cada retención y los comprobantes de ISLR y de IVA se calculan solos.
        </p>
      </header>

      {/* (22/10) El catálogo de ISLR del Decreto 1.808, a consultar al lado de los
          comprobantes: es la misma tabla que propone la alícuota en la orden de pago. */}
      <div className="no-print mb-6">
        <Pestanas
          pestanas={SECCIONES}
          activa={seccion}
          onCambiar={setSeccion}
          etiqueta="Retenciones"
          idPanel="retenciones-panel"
          prefijoId="retenciones"
        />
      </div>

      {seccion === "catalogo" ? (
        <div
          id="retenciones-panel"
          role="tabpanel"
          aria-labelledby="retenciones-catalogo"
          className="no-print"
        >
          <CatalogoIslr />
        </div>
      ) : (
        <>
          <div className="no-print grid gap-6 xl:grid-cols-[minmax(0,30rem)_1fr]">
            <section className="grid content-start gap-4">
              {puedeLeer && (
                <fieldset className="flex gap-2">
                  <legend className="sr-only">Cómo llenar el comprobante</legend>
                  {(
                    [
                      ["manual", "Escribir a mano"],
                      ["ia", "Leer documentos con IA"],
                    ] as const
                  ).map(([clave, rotulo]) => (
                    <button
                      key={clave}
                      type="button"
                      aria-pressed={modo === clave}
                      className={
                        modo === clave
                          ? "boton-primario h-9 px-3 text-sm"
                          : "boton-secundario h-9 px-3 text-sm"
                      }
                      onClick={() => setModo(clave)}
                    >
                      {rotulo}
                    </button>
                  ))}
                </fieldset>
              )}
              {puedeLeer && modo === "ia" && (
                <LectorRetencion
                  porcentajes={borrador}
                  onVolcar={(cambios) => {
                    setBorrador({ ...borrador, ...cambios })
                    setAviso("Volcado desde la lectura: revísalo antes de guardar.")
                  }}
                />
              )}
              <EditorRetencion datos={borrador} onCambio={setBorrador} />
              <LineasRetencion datos={borrador} onCambio={setBorrador} montos={montosEmision} />
            </section>

            <section className="grid content-start gap-4">
              <div className="superficie p-4">
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <h2 className="rotulo flex-1">
                    {editando ? editando.codigo : "Nuevo comprobante"}
                  </h2>
                  {puedeEditar && (
                    <Boton
                      variante="primario"
                      disabled={falta !== null || guardar.isPending}
                      title={falta ?? undefined}
                      onClick={() => guardar.mutate(borrador)}
                    >
                      <Save size={15} aria-hidden={true} />
                      {editando ? "Guardar cambios" : "Guardar"}
                    </Boton>
                  )}
                  {[
                    ...TIPOS_COMPROBANTE.map((t) => [t, `Imprimir ${t}`] as const),
                    ["AMBOS", "Imprimir ambos"] as const,
                  ].map(([clave, rotulo]) => (
                    <Boton
                      key={clave}
                      disabled={calculo.data === undefined}
                      onClick={() => imprimir(clave === "AMBOS" ? [...TIPOS_COMPROBANTE] : [clave])}
                    >
                      <Printer size={15} aria-hidden={true} />
                      {rotulo}
                    </Boton>
                  ))}
                  {editando && (
                    <Boton onClick={empezarOtro}>
                      <Plus size={15} aria-hidden={true} />
                      Empezar otro
                    </Boton>
                  )}
                </div>
                {falta && puedeEditar && (
                  <p className="mb-2 text-xs" style={{ color: "var(--fg-muted)" }}>
                    Para guardar: {falta}
                  </p>
                )}
                {aviso && (
                  <p className="mb-2 text-sm" role="status">
                    {aviso}
                  </p>
                )}
                {calculo.error && (
                  <p className="mb-2 text-sm" role="alert" style={{ color: "var(--destructive)" }}>
                    No se pudo calcular: {calculo.error.message}
                  </p>
                )}
                {calculo.data?.reajuste && <ComparacionReajuste e={calculo.data} />}
                <fieldset className="mb-2 flex flex-wrap gap-2">
                  <legend className="sr-only">Qué comprobante ver</legend>
                  {TIPOS_COMPROBANTE.map((t) => (
                    <button
                      key={t}
                      type="button"
                      aria-pressed={vista === t}
                      className={
                        vista === t
                          ? "boton-primario h-9 px-3 text-sm"
                          : "boton-secundario h-9 px-3 text-sm"
                      }
                      onClick={() => setVista(t)}
                    >
                      Comprobante {t}
                      {retenido[t] !== undefined && (
                        <span className="tabular-nums"> · {monto(retenido[t] ?? "0")}</span>
                      )}
                    </button>
                  ))}
                </fieldset>
                {/* A escala real y con desplazamiento: lo que se ve es lo que se imprime. */}
                <div
                  className="overflow-auto"
                  style={{ background: "var(--surface-elevated)", padding: "1rem" }}
                >
                  <div style={{ width: "fit-content" }}>{pieza(vista)}</div>
                </div>
              </div>

              <div className="superficie p-4">
                <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                  <h2 className="rotulo">Guardados</h2>
                  {lista.data && lista.data.length > 0 && (
                    <Exportar
                      titulo="Retenciones provisionales"
                      documento={() => documentoGuardados(lista.data ?? [], busqueda)}
                    />
                  )}
                </div>
                <input
                  className="campo mb-3 h-9 w-full text-sm"
                  placeholder="Buscar por código, cliente, beneficiario, documento o B/L"
                  aria-label="Buscar comprobantes guardados"
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                />
                {lista.isPending ? (
                  <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
                    Cargando…
                  </p>
                ) : lista.error ? (
                  <p className="text-sm" role="alert" style={{ color: "var(--destructive)" }}>
                    {lista.error.message}
                  </p>
                ) : lista.data.length === 0 ? (
                  <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
                    {busqueda
                      ? "Ningún comprobante coincide con la búsqueda."
                      : "Todavía no hay comprobantes guardados."}
                  </p>
                ) : (
                  <ul className="grid gap-2">
                    {lista.data.map((r) => (
                      <li key={r.id} className="tarjeta flex items-center gap-2 p-2">
                        <button
                          type="button"
                          className="min-w-0 flex-1 text-left"
                          onClick={() => abrir.mutate(r.id)}
                        >
                          <span className="block truncate text-sm font-medium">
                            {r.codigo} · {r.beneficiario_nombre}
                          </span>
                          <span
                            className="tabular-nums block truncate text-xs"
                            style={{ color: "var(--fg-muted)" }}
                          >
                            {comoFecha(r.fecha)} · {r.agente_nombre}
                            {r.documento ? ` · ${r.documento}` : ""} · retenido{" "}
                            {monto(r.total_retenido)} · pagado {monto(r.neto)}
                            {r.reajustado ? " · reajustado" : ""}
                          </span>
                        </button>
                        {puedeEditar && (
                          <button
                            type="button"
                            className="boton-secundario grid h-9 w-9 shrink-0 place-items-center"
                            aria-label={`Retirar ${r.codigo}`}
                            onClick={async () => {
                              const si = await confirmar({
                                titulo: "Retirar el comprobante",
                                mensaje: `¿Retirar ${r.codigo}? Deja de salir en la lista.`,
                                aceptar: "Retirar",
                                peligro: true,
                              })
                              if (si) borrar.mutate(r.id)
                            }}
                          >
                            <Trash2 size={16} aria-hidden={true} />
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>
          </div>

          {/* Fuera del lienzo en pantalla; es lo único que sale en el papel. Una hoja por
          comprobante: `.pliego-hoja` lleva el salto de página entre una y otra. */}
          <div className="pliego-impresion pliego-retenciones" aria-hidden={true}>
            {aImprimir.map((t) => (
              <div key={t} className="pliego-hoja">
                {pieza(t)}
              </div>
            ))}
          </div>
          {confirmacion}
        </>
      )}
    </div>
  )
}

/** Lo que el servidor necesita para calcular: líneas válidas, porcentajes y tasas. */
function aCalcular(b: BorradorRetencion) {
  const {
    lineas,
    alicuota_iva,
    porcentaje_ret_iva,
    porcentaje_ret_islr,
    sustraendo_islr,
    tasa_cambio,
    tasa_reajuste,
  } = paraElServidor(b)
  return {
    lineas,
    alicuota_iva,
    porcentaje_ret_iva,
    porcentaje_ret_islr,
    sustraendo_islr,
    tasa_cambio,
    tasa_reajuste,
  }
}

/** Emisión, reajuste y diferencia, lado a lado. Las cifras son del servidor. */
function ComparacionReajuste({ e }: { e: Evaluacion }) {
  const r = e.reajuste
  if (!r) return null
  const filas = [
    ["Total factura", e.total, r.calculo.total, r.diferencia_total],
    ["Retenciones", e.total_retenido, r.calculo.total_retenido, r.diferencia_retenido],
    ["Neto a pagar", e.neto, r.calculo.neto, r.diferencia_neto],
  ] as const
  return (
    <div className="mb-3 overflow-x-auto">
      <table className="w-full text-sm">
        <caption className="mb-1 text-left text-xs" style={{ color: "var(--fg-muted)" }}>
          Reajuste cambiario: de {tasaLegible(r.tasa_emision)} a {tasaLegible(r.tasa_reajuste)}{" "}
          Bs/USD. El comprobante sale reajustado.
        </caption>
        <thead>
          <tr className="text-xs" style={{ color: "var(--fg-muted)" }}>
            <th className="py-1 text-left font-medium" />
            <th className="py-1 text-right font-medium">A emisión</th>
            <th className="py-1 text-right font-medium">Reajustado</th>
            <th className="py-1 text-right font-medium">Diferencia</th>
          </tr>
        </thead>
        <tbody>
          {filas.map(([rotulo, antes, despues, diferencia]) => (
            <tr key={rotulo} className="border-t" style={{ borderColor: "var(--border)" }}>
              <th scope="row" className="py-1 text-left font-medium">
                {rotulo}
              </th>
              <td className="tabular-nums py-1 text-right">{monto(antes)}</td>
              <td className="tabular-nums py-1 text-right font-semibold">{monto(despues)}</td>
              <td className="tabular-nums py-1 text-right">{monto(diferencia)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** «872.392700» → «872,3927». Sólo para leer. */
function tasaLegible(t: string): string {
  const limpio = t.includes(".") ? t.replace(/0+$/, "").replace(/\.$/, "") : t
  return limpio.replace(".", ",")
}

/** Qué falta para poder guardar, dicho en palabras; `null` si nada. */
function faltante(b: BorradorRetencion): string | null {
  if (!b.agente_nombre.trim()) return "el nombre del agente de retención."
  if (!b.beneficiario_nombre.trim()) return "el nombre del beneficiario."
  if (!b.fecha) return "la fecha."
  if (paraElServidor(b).lineas.length === 0) return "al menos un servicio con su monto."
  return null
}
